import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { getAuth } from 'firebase/auth'
import { TransformLearningService } from '../services/TransformLearningService'
import { TransformLearningProgressService } from '../services/TransformLearningProgressService'
import { useTransformOwnerContext } from '../composables/useTransformOwnerContext'
import { handleErrors } from '../utils/errorHandler'
import navigationMonitor from '../services/NavigationMonitorService'
import { formatDuration } from '../utils/durationHelpers'
import { resolveItemsPercent } from '../utils/transformProgressPercent'

// OQ-TMD-1 RESOLVED (confirmed in Firestore QA, 2026-06-30):
// • bcst_assessments.lessonId always points to the quiz lesson itself (lessonType='quiz').
// • For every quiz in the course (24 total), the lesson at position - 1 is always a video.
// • There is no explicit "requiredLessonId" field; the video↔quiz link is purely positional.
// Lock rule: a quiz at position P is locked if the video at position P-1 in the same module
// is not completed (lessonProgressMap[videoLesson.id]?.completed !== true).

// Fallback pass mark used when an assessment does not define passThresholdPercent.
const DEFAULT_QUIZ_PASS_THRESHOLD_PERCENT = 75

// A lesson counts as truly completed for progress/next-in-queue purposes when:
// • it is not a quiz and progress.completed === true, OR
// • it is a quiz, progress.completed === true, AND the score meets the pass threshold.
function isLessonTrulyCompleted(lesson, progress, assessment = null) {
  if (!progress?.completed) return false
  if (lesson.lessonType !== 'quiz') return true
  // Prefer the authoritative pass flag persisted by the server (submitQuiz). Fall back
  // to comparing the stored score against the threshold for legacy docs written before
  // `passed` was persisted.
  if (progress.passed != null) return progress.passed === true
  if (progress.scorePercent == null) return false
  const threshold = assessment?.passThresholdPercent ?? DEFAULT_QUIZ_PASS_THRESHOLD_PERCENT
  return progress.scorePercent >= threshold
}

// lastActivityAt is a Firestore Timestamp when progress was read client-side (self path)
// but epoch millis when it came through the gateway JSON (admin view-as path) — accept both.
function progressActivityMs(progress) {
  const value = progress?.lastActivityAt
  if (value?.toMillis) return value.toMillis()
  return typeof value === 'number' ? value : 0
}

// Firebase Storage path for a lesson thumbnail.
export function lessonThumbnailPath(lesson) {
  return `bcst/${lesson.courseId}/lessons/${lesson.id}/thumbnail/${lesson.thumbnailFileName}`
}

// Firebase Storage path for a module banner image.
export function moduleImagePath(module) {
  return `bcst/${module.courseId}/modules/${module.id}/thumbnail/${module.thumbnailFileName}`
}

export const useTransformLearningStore = defineStore('transformLearning', () => {
  const course = ref(null)
  const modules = ref([])
  const lessonsByModule = ref({})
  const isLoading = ref(false)
  const error = ref(null)
  const loaded = ref(false)

  // Admin "view-as": while active, all progress reads target ownerContext.ownerUid.
  // Instantiating the gateway client here is safe even though it uses inject():
  // Pinia runs this setup inside app.runWithContext, so app-level provides resolve.
  const ownerContext = useTransformOwnerContext()
  const progressGateway = TransformLearningProgressService()

  // Whose progress lessonProgressMap currently holds — detects a stale cache when the
  // view-as context is entered/left/switched between loads.
  const progressFetchedFor = ref(null)

  // Reports to Sentry when the Transform views stay loading past the timeout
  // (notably the infinite-loading state after returning via browser Back).
  navigationMonitor.watchLoading('transformLearning', isLoading)

  // Map lessonId → progress doc from bcst_lesson_progress
  const lessonProgressMap = ref({})

  // Quiz/assessment data — populated lazily by loadCategoryDetail().
  // assessmentsByLessonId: quizLessonId → assessment doc { id, lessonId, passThresholdPercent, ... }
  // questionCountsByAssessmentId: assessmentId → number of questions
  // quizDataLoadedByModule: moduleId → true once assessment data for that module is cached
  const assessmentsByLessonId = ref({})
  const questionCountsByAssessmentId = ref({})
  const quizDataLoadedByModule = ref({})

  // Lesson count is derived from the published lessons actually loaded, not from the
  // module's denormalized `lessonCount` field, so the count stays consistent with the
  // progress bar and detail view (both of which only count published lessons).
  const modulesWithLessonCount = computed(() =>
    modules.value.map(m => ({
      ...m,
      lessonCount: lessonsByModule.value[m.id]?.length ?? 0,
    }))
  )

  // Overall course progress — derived from lessonProgressMap + catalog, read-only.
  // A lesson is a video OR a quiz: the course has 48 of them and they are never grouped into
  // video+quiz pairs. The pairing survives only as the quiz LOCK rule (a quiz opens once the
  // video before it is completed), which has nothing to do with counting.
  const courseLessons = computed(() =>
    Object.values(lessonsByModule.value)
      .flat()
      .filter(l => l.lessonType === 'video' || l.lessonType === 'quiz')
  )

  const totalLessons = computed(() => courseLessons.value.length)

  // A video lesson counts when watched, a quiz lesson when passed.
  const completedLessons = computed(() =>
    courseLessons.value.filter(l =>
      isLessonTrulyCompleted(l, lessonProgressMap.value[l.id], assessmentsByLessonId.value[l.id])
    ).length
  )

  const overallPercent = computed(() => resolveItemsPercent(completedLessons.value, totalLessons.value))

  const lessonsRemaining = computed(() => totalLessons.value - completedLessons.value)

  // Determines the next lesson to continue (T06).
  // Strategy: sequential by module position — iterate modules in order, pick the first with
  // incomplete non-text lessons, then within that module prefer the most recently accessed
  // (lastActivityAt) lesson that has a progress doc. Falls back to the first by position.
  // This keeps users progressing through the curriculum in order rather than jumping ahead
  // because a later-module lesson happens to have a more recent lastActivityAt timestamp.
  // Note: no early return on empty progress — a brand-new user (zero progress docs)
  // still gets the very first incomplete lesson so the card can render "Start Here".
  const continueLesson = computed(() => {
    if (!modules.value.length) return null

    const sortedModules = [...modules.value].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

    for (const mod of sortedModules) {
      const modLessons = (lessonsByModule.value[mod.id] ?? [])
        .filter(l => l.lessonType !== 'text')
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

      const incompleteLessons = modLessons.filter(
        l => !isLessonTrulyCompleted(l, lessonProgressMap.value[l.id], assessmentsByLessonId.value[l.id])
      )

      if (!incompleteLessons.length) continue

      // Within this module: prefer the lesson with the most recent actual progress,
      // fall back to first by position if no lesson has a progress doc yet.
      const inProgress = incompleteLessons
        .filter(l => lessonProgressMap.value[l.id])
        .sort((a, b) => progressActivityMs(lessonProgressMap.value[b.id]) - progressActivityMs(lessonProgressMap.value[a.id]))

      const target = inProgress[0] ?? incompleteLessons[0]

      // Enrich the lesson (status, durationLabel, quiz.{questions,locked,…}) via categoryDetail
      // so the dashboard continue card consumes the exact same shape as the category variant.
      // Numbering comes from the enriched lesson's per-type displayNumber (Lesson N / Quiz N),
      // and the total matches the lesson's own type for the "Lesson X of Y" title.
      const detail = categoryDetail(mod.id)
      const enriched = detail?.lessons.find(l => l.id === target.id) ?? target
      const lessonIndex = enriched?.displayNumber ?? null
      const lessonTotal = enriched?.lessonType === 'quiz'
        ? (detail?.quizTotal ?? null)
        : (detail?.videoTotal ?? null)

      return {
        lesson: enriched,
        progress: lessonProgressMap.value[target.id] ?? null,
        module: mod,
        lessonIndex: lessonIndex && lessonIndex > 0 ? lessonIndex : null,
        lessonTotal,
      }
    }

    return null
  })

  const progressByModule = computed(() => {
    const result = {}
    for (const mod of modules.value) {
      const detail = categoryDetail(mod.id)
      const lessonCount = detail?.total ?? 0
      const completedCount = detail?.completedCount ?? 0
      const percent = detail?.percent ?? 0
      result[mod.id] = { completedCount, lessonCount, percent }
    }
    return result
  })

  // Pure computation — derives enriched lesson state from reactive refs.
  function buildCategoryDetail(moduleId) {
    const module = modules.value.find(m => m.id === moduleId)
    if (!module) return null

    const rawLessons = [...(lessonsByModule.value[moduleId] ?? [])].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    )


    // OQ-TMD-1: a quiz's prerequisite is the closest preceding video by position.
    // Precomputed in one pass (rawLessons is position-sorted) to avoid a per-quiz filter+sort.
    const requiredVideoByQuizId = {}
    let precedingVideo = null
    for (const l of rawLessons) {
      if (l.lessonType === 'video') precedingVideo = l
      else if (l.lessonType === 'quiz') requiredVideoByQuizId[l.id] = precedingVideo
    }

    const lessons = rawLessons.map(lesson => {
      const progress = lessonProgressMap.value[lesson.id] ?? null
      const assessment = assessmentsByLessonId.value[lesson.id] ?? null
      const isTrulyCompleted = isLessonTrulyCompleted(lesson, progress, assessment)
      const progressCompleted = progress?.completed === true

      if (lesson.lessonType === 'video') {
        const uniquePct = progress?.uniquePercentWatched ?? 0
        const isInProgress = !progressCompleted && uniquePct > 0
        // How much of the video has been watched (for the "Watched: Xm Ys" meta).
        const watchedSeconds = Math.round((uniquePct / 100) * (lesson.videoDurationSeconds ?? 0))
        return {
          ...lesson,
          status: isTrulyCompleted ? 'watched' : isInProgress ? 'in_progress' : 'not_started',
          durationLabel: formatDuration(lesson.videoDurationSeconds ?? 0),
          watchedLabel: formatDuration(watchedSeconds),
          quiz: null,
        }
      }

      if (lesson.lessonType === 'quiz') {
        const assessmentId = assessment?.id ?? null
        const questions = assessmentId
          ? (questionCountsByAssessmentId.value[assessmentId] ?? 0)
          : 0
        const scorePercent = progress?.scorePercent ?? null
        const threshold = assessment?.passThresholdPercent ?? DEFAULT_QUIZ_PASS_THRESHOLD_PERCENT
        // Prefer the server-persisted pass flag; fall back to the score comparison for
        // legacy progress docs written before `passed` was stored.
        const passed = progress?.passed != null
          ? progress.passed === true
          : (progressCompleted && scorePercent !== null && scorePercent >= threshold)

        const requiredLesson = requiredVideoByQuizId[lesson.id] ?? null

        const locked = requiredLesson
          ? !lessonProgressMap.value[requiredLesson.id]?.completed
          : false

        let status
        if (progressCompleted) {
          status = passed ? 'passed' : 'failed'
        } else if (locked) {
          status = 'locked'
        } else if (progress != null) {
          // A quiz with a progress doc (even a freshly reset retake) is considered
          // in progress; only a completely absent progress doc is not_started.
          status = 'in_progress'
        } else {
          status = 'not_started'
        }

        return {
          ...lesson,
          status,
          descriptionHtml: assessment?.descriptionHtml ?? null,
          durationLabel: null,
          quiz: {
            assessmentId,
            questions,
            scorePercent,
            passed,
            locked,
            requiredLessonId: requiredLesson?.id ?? null,
            requiredLessonPosition: requiredLesson?.position ?? null,
          },
        }
      }

      // text type — no quiz data, just completion state
      return {
        ...lesson,
        status: isTrulyCompleted ? 'watched' : 'not_started',
        durationLabel: null,
        quiz: null,
      }
    })

    // Presentation-layer numbering: videos and quizzes are numbered independently
    // (Lesson 1,2,3… / Quiz 1,2,3…) while preserving the position-based order. This is
    // the single source every view reads (`displayNumber`), so the numbering shown to
    // users stays consistent everywhere and never depends on the raw mixed position.
    let videoSeq = 0
    let quizSeq = 0
    for (const l of lessons) {
      if (l.lessonType === 'video') l.displayNumber = ++videoSeq
      else if (l.lessonType === 'quiz') l.displayNumber = ++quizSeq
      else l.displayNumber = null
    }
    const videoTotal = videoSeq
    const quizTotal = quizSeq

    const nextInQueue = lessons.find(l => !['watched', 'passed'].includes(l.status)) ?? null

    // Every video and every quiz of the module is one lesson.
    const total = videoTotal + quizTotal
    const completedCount = lessons.filter(l =>
      (l.lessonType === 'video' && l.status === 'watched') ||
      (l.lessonType === 'quiz' && l.status === 'passed')
    ).length

    // The percentage is granular: every video and quiz of the module counts on its own.
    const moduleItems = lessons.filter(l => l.lessonType === 'video' || l.lessonType === 'quiz')
    const completedItemCount = moduleItems.filter(l =>
      (l.lessonType === 'video' && l.status === 'watched') ||
      (l.lessonType === 'quiz' && l.status === 'passed')
    ).length
    const percent = resolveItemsPercent(completedItemCount, moduleItems.length)

    return { module, lessons, total, percent, completedCount, nextInQueue, videoTotal, quizTotal }
  }

  // Memoized enriched detail for every module, rebuilt only when the underlying
  // catalog/progress/assessment state changes. Callers (continueLesson, progressByModule,
  // the lesson-detail view) share these results within a reactivity cycle instead of each
  // re-running buildCategoryDetail on every access.
  const categoryDetailByModuleId = computed(() => {
    const result = {}
    for (const mod of modules.value) {
      result[mod.id] = buildCategoryDetail(mod.id)
    }
    return result
  })

  // Enriched detail for one module (status, quiz data, ordering). Reads the memoized map.
  function categoryDetail(moduleId) {
    return categoryDetailByModuleId.value[moduleId] ?? null
  }

  // Finds a single lesson by id across all modules (raw catalog doc, not enriched).
  // Returns null when the catalogue is not loaded yet or the id is unknown.
  function getLessonById(lessonId) {
    if (!lessonId) return null
    return Object.values(lessonsByModule.value).flat().find(l => l.id === lessonId) ?? null
  }

  // Merges a progress patch into lessonProgressMap so the UI (badges, progress bars,
  // continue card) reflects a just-persisted change without a full reload.
  // The backend is the source of truth; this only mirrors what was successfully saved.
  function applyLessonProgress(lessonId, patch = {}) {
    if (!lessonId) return
    const current = lessonProgressMap.value[lessonId] ?? { lessonId }
    lessonProgressMap.value = {
      ...lessonProgressMap.value,
      [lessonId]: { ...current, ...patch },
    }
  }

  // Loads assessment + question-count data for all quiz lessons in a module.
  // No-op if already cached (unless forceUpdate). Call after loadDashboard().
  async function loadCategoryDetail(moduleId, forceUpdate = false) {
    if (quizDataLoadedByModule.value[moduleId] && !forceUpdate) return

    try {
      const svc = TransformLearningService()
      const moduleLessons = lessonsByModule.value[moduleId] ?? []
      const quizLessonIds = moduleLessons
        .filter(l => l.lessonType === 'quiz')
        .map(l => l.id)

      if (!quizLessonIds.length) {
        quizDataLoadedByModule.value = { ...quizDataLoadedByModule.value, [moduleId]: true }
        return
      }

      const assessments = await svc.getAssessmentsByLessonIds(quizLessonIds)

      const updatedAssessments = { ...assessmentsByLessonId.value }
      for (const a of assessments) updatedAssessments[a.lessonId] = a
      assessmentsByLessonId.value = updatedAssessments

      const assessmentIds = assessments.map(a => a.id)
      if (assessmentIds.length) {
        const counts = await svc.getQuizQuestionCountsByAssessmentIds(assessmentIds)
        questionCountsByAssessmentId.value = { ...questionCountsByAssessmentId.value, ...counts }
      }

      quizDataLoadedByModule.value = { ...quizDataLoadedByModule.value, [moduleId]: true }
    } catch (err) {
      error.value = err?.message ?? 'Failed to load category detail.'
      handleErrors(`TransformLearningStore.loadCategoryDetail-${moduleId}`, err)
    }
  }

  // The uid the next progress fetch will target: the view-as owner when active,
  // otherwise the authenticated user.
  function effectiveUid() {
    return ownerContext.isActive.value ? ownerContext.ownerUid.value : (getAuth().currentUser?.uid ?? null)
  }

  // Progress fetches overlap: whoever changes the view-as context does not await the
  // ownerUid watch below, so closing one learner's panel can start a self refresh while
  // opening the next learner starts a target one. Every fetch takes a sequence number and
  // only the latest may write its result — an older response arriving late is dropped
  // instead of replacing the current target's progress. A fetch for the target already in
  // flight is joined rather than started again, so the watch and loadDashboard never race
  // each other for the same user.
  let progressRequestSeq = 0
  let progressInFlight = null // { target, courseId, requestId, promise }

  function isCurrentProgressRequest(requestId) {
    return requestId === progressRequestSeq
  }

  function invalidateProgressRequests() {
    progressRequestSeq += 1
    progressInFlight = null
  }

  // Fetches the effective user's progress docs for a course. Self keeps the direct
  // Firestore query (unchanged path); the view-as target goes through the gateway,
  // because rules scope client reads of bcst_lesson_progress to the caller's own docs.
  // Resolves to { docs, fetchedFor }, or null when a newer request superseded this one.
  async function fetchProgressDocs(courseId, requestId) {
    if (ownerContext.isActive.value) {
      const ownerUid = ownerContext.ownerUid.value
      const data = await progressGateway.getCourseProgress(courseId, ownerUid)
      if (!isCurrentProgressRequest(requestId)) return null
      if (data && data.ownerResolved !== false) {
        ownerContext.setTargetUser(data.targetUser)
        return { docs: data.progress ?? [], fetchedFor: ownerUid }
      }
      // The server refused the owner (not allowed to see that user) or the user doesn't
      // exist. Exit the context — silently presenting the caller's own data as the target's
      // would be worse than dropping back to the normal self view — and load self below.
      ownerContext.exit()
    }
    const uid = getAuth().currentUser?.uid
    if (!uid) return { docs: [], fetchedFor: null }
    const progressDocs = await TransformLearningService().getProgressByCourse(courseId, uid)
    if (!isCurrentProgressRequest(requestId)) return null
    return { docs: progressDocs, fetchedFor: uid }
  }

  // Starts the progress fetch for the current effective user, or joins the one in flight.
  function requestProgress(courseId) {
    const target = effectiveUid()
    if (progressInFlight && progressInFlight.target === target && progressInFlight.courseId === courseId) {
      return progressInFlight.promise
    }
    progressRequestSeq += 1
    const requestId = progressRequestSeq
    const promise = fetchProgressDocs(courseId, requestId).finally(() => {
      if (progressInFlight?.requestId === requestId) progressInFlight = null
    })
    progressInFlight = { target, courseId, requestId, promise }
    return promise
  }

  // Records whose docs the map holds so a context switch invalidates the cache.
  function applyProgressResult({ docs, fetchedFor }) {
    const progressMap = {}
    for (const doc of docs) progressMap[doc.lessonId] = doc
    lessonProgressMap.value = progressMap
    progressFetchedFor.value = fetchedFor
  }

  // Re-fetches only the user's progress for the active course, leaving the (stable)
  // catalog untouched. Lets views refresh a possibly-stale "Watched"/completion state
  // on mount without refetching the whole course + modules + lessons every time.
  async function refreshProgress() {
    if (!course.value?.id || !effectiveUid()) return
    error.value = null
    try {
      const result = await requestProgress(course.value.id)
      if (result) applyProgressResult(result)
    } catch (err) {
      error.value = err?.message ?? 'Failed to refresh progress.'
      handleErrors('TransformLearningStore.refreshProgress', err)
    }
  }

  // Entering/leaving the view-as context (or switching target) while the catalog is
  // already loaded must swap the progress data even if no view remounts.
  watch(() => ownerContext.ownerUid.value, () => {
    if (course.value?.id && progressFetchedFor.value !== effectiveUid()) refreshProgress()
  })

  async function loadDashboard(forceUpdate = false) {
    if (loaded.value && !forceUpdate) {
      // Warm catalog cache, but the cached progress may belong to a different user than
      // the current (view-as) context targets — refresh just the progress in that case.
      if (progressFetchedFor.value !== effectiveUid()) await refreshProgress()
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const svc = TransformLearningService()

      const fetchedCourse = await svc.getActiveCourse()
      if (!fetchedCourse) {
        error.value = 'No published course found.'
        return
      }
      course.value = fetchedCourse

      const fetchParams = [
        svc.getModulesByCourse(fetchedCourse.id),
        svc.getLessonsByCourse(fetchedCourse.id),
      ]
      if (effectiveUid()) fetchParams.push(requestProgress(fetchedCourse.id))

      // No effective user → empty progress. A null result means a newer progress request
      // superseded this one; that request writes the map, so it is left alone here.
      const [fetchedModules, allLessons, progressResult = { docs: [], fetchedFor: null }] = await Promise.all(fetchParams)

      const grouped = {}
      for (const lesson of allLessons) {
        const mid = lesson.moduleId
        if (!grouped[mid]) grouped[mid] = []
        grouped[mid].push(lesson)
      }
      lessonsByModule.value = grouped

      // Business rule: a category is shown to the user ONLY if it has at least one
      // published lesson. getLessonsByCourse already returns published lessons only, so a
      // module with no entry in `grouped` has none published — drop it here at the source
      // so every surface derived from `modules` (dashboard grid, progress panel, continue
      // card, module detail, search) excludes empty categories by construction.
      modules.value = fetchedModules.filter(m => (grouped[m.id]?.length ?? 0) > 0)

      if (progressResult) applyProgressResult(progressResult)

      loaded.value = true
    } catch (err) {
      error.value = err?.message ?? 'Failed to load learning dashboard.'
      handleErrors('TransformLearningStore.loadDashboard', err)
    } finally {
      isLoading.value = false
    }
  }

  // Clears all cached catalog/progress/quiz state. Called on logout so a subsequent
  // user never sees the previous user's course data (see useLogout).
  function reset() {
    invalidateProgressRequests()
    course.value = null
    modules.value = []
    lessonsByModule.value = {}
    lessonProgressMap.value = {}
    progressFetchedFor.value = null
    assessmentsByLessonId.value = {}
    questionCountsByAssessmentId.value = {}
    quizDataLoadedByModule.value = {}
    isLoading.value = false
    error.value = null
    loaded.value = false
  }

  return {
    course,
    modules,
    lessonsByModule,
    lessonProgressMap,
    isLoading,
    error,
    loaded,
    modulesWithLessonCount,
    progressByModule,
    totalLessons,
    completedLessons,
    overallPercent,
    lessonsRemaining,
    continueLesson,
    assessmentsByLessonId,
    questionCountsByAssessmentId,
    quizDataLoadedByModule,
    categoryDetail,
    getLessonById,
    applyLessonProgress,
    loadDashboard,
    loadCategoryDetail,
    refreshProgress,
    reset,
  }
})
